import { getConsolidatedPATStatusLabel } from "@/features/person/utils/pat-status";

export interface PATStatusResult {
  value: string;
  variant: "success" | "warn" | "error" | "surface";
}

export interface PATStatusMetricResult {
  value: string;
  variant: "success" | "warn" | "error" | "info";
}

/**
 * Builds PAT status for students.
 *
 * Trusts the backend-computed `status` (and `expired`) fields rather than
 * re-deriving status from the `submissions` array. The backend
 * `playingAbilityTestStatus.status` is computed via PAT::Status#most_recent,
 * which is the same source of truth the BE filter resolves against.
 *
 * @param playingAbilityTestStatus - The PAT status data from the API
 * @returns Object containing the status value and variant for display
 */
export function buildPATStatus(playingAbilityTestStatus?: any): PATStatusResult {
  if (!playingAbilityTestStatus) {
    return { value: getConsolidatedPATStatusLabel("not_started"), variant: "surface" };
  }

  if (playingAbilityTestStatus.activeSuspension) {
    return { value: getConsolidatedPATStatusLabel("suspended"), variant: "error" };
  }

  const normalizedStatus = playingAbilityTestStatus.status?.toLowerCase();
  const expired = playingAbilityTestStatus.expired === true;

  if (normalizedStatus === "passed" && !expired) {
    return { value: getConsolidatedPATStatusLabel("passed"), variant: "success" };
  }

  if (expired) {
    return { value: getConsolidatedPATStatusLabel("expired"), variant: "error" };
  }

  if (normalizedStatus === "qualified") {
    return { value: getConsolidatedPATStatusLabel("qualifying"), variant: "warn" };
  }

  if (normalizedStatus === "failed") {
    return { value: getConsolidatedPATStatusLabel("failed"), variant: "error" };
  }

  if (normalizedStatus === "registered") {
    return { value: "Registered", variant: "warn" };
  }

  if (normalizedStatus === "pending") {
    return { value: "Pending", variant: "warn" };
  }

  if (playingAbilityTestStatus.status !== null && playingAbilityTestStatus.status !== undefined) {
    return { value: getConsolidatedPATStatusLabel("in_progress"), variant: "warn" };
  }

  if (playingAbilityTestStatus.submissions?.length > 0) {
    return { value: getConsolidatedPATStatusLabel("in_progress"), variant: "warn" };
  }

  return { value: getConsolidatedPATStatusLabel("not_started"), variant: "surface" };
}

/**
 * Builds PAT status for MetricCard usage (maps surface to info)
 * @param playingAbilityTestStatus - The PAT status data from the API
 * @returns Object containing the status value and variant for MetricCard display
 */
export function buildPATStatusForMetric(playingAbilityTestStatus?: any): PATStatusMetricResult {
  const result = buildPATStatus(playingAbilityTestStatus);
  return {
    value: result.value,
    variant: result.variant === "surface" ? "info" : result.variant
  };
}
